const RTDB_KEY_FORBIDDEN = /[.#$[\]/\u0000-\u001F\u007F]/
const MAX_RTDB_KEY_BYTES = 768

const DATE_PART = /^\d{4}-\d{2}-\d{2}$/
const TIME_PART = /^\d{2}:\d{2}$/
const DISPLAY_DATE_TIME = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/
const CSV_DATE_TIME = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{1,2}):(\d{2})$/
const CSV_DATE_TIME_WITH_SECONDS = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2})$/
const SPREADSHEET_SERIAL = /^(\d+)(?:[.,](\d+))?$/
const NUMERIC = /^[+-]?\d+(\.\d+)?$/
const SPREADSHEET_EPOCH_UTC = Date.UTC(1899, 11, 30)
const SPREADSHEET_MIN_DAY = 36526
const SPREADSHEET_MAX_DAY = 73415

/**
 * Nombre usable como clave de Firebase Realtime Database.
 * @param {string} name
 * @returns {boolean}
 */
export const isValidCouponName = name => {
  if (!name || name === '.' || name === '..') return false
  if (RTDB_KEY_FORBIDDEN.test(name)) return false
  return new TextEncoder().encode(name).length <= MAX_RTDB_KEY_BYTES
}

/**
 * Interpreta `DD/MM/YYYY HH:MM` como fecha local, igual que el alta individual.
 * @param {string} value
 * @returns {{ date: string, time: string, timestamp: number } | null}
 */
export const parseCouponDisplayDateTime = value => {
  if (typeof value !== 'string') return null
  const match = DISPLAY_DATE_TIME.exec(value.trim())
  if (!match) return null

  const day = Number(match[1])
  const month = Number(match[2])
  const year = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  if (month < 1 || month > 12 || hour > 23 || minute > 59) return null

  const date = `${match[3]}-${match[2]}-${match[1]}`
  const time = `${match[4]}:${match[5]}`
  const timestamp = parseLocalDateTime(date, time)
  if (timestamp == null) return null

  const check = new Date(timestamp)
  if (
    check.getFullYear() !== year ||
    check.getMonth() + 1 !== month ||
    check.getDate() !== day ||
    check.getHours() !== hour ||
    check.getMinutes() !== minute
  ) {
    return null
  }

  return { date, time, timestamp }
}

/**
 * Fecha de una celda CSV, ya sin el apóstrofo de texto de la plantilla.
 * Acepta `DD/MM/YYYY HH:MM`, hora de un dígito (`H:MM`), la misma hora con
 * segundos `:00`, o un serial de hoja de cálculo entre 2000-01-01 y 2100-12-31.
 * El serial se lee como calendario (UTC solo para la cuenta de días) y luego se
 * interpreta en hora local, igual que el alta individual.
 * @param {string} value
 * @returns {{ date: string, time: string, timestamp: number } | null}
 */
export const parseCouponCsvDate = value => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  const canonical = parseFlexibleDisplayDateTime(trimmed)
  if (canonical) return canonical

  const withZeroSeconds = parseDateTimeWithZeroSeconds(trimmed)
  if (withZeroSeconds) return withZeroSeconds

  return parseSpreadsheetSerial(trimmed)
}

const parseFlexibleDisplayDateTime = value => {
  const match = CSV_DATE_TIME.exec(value)
  if (!match) return null
  const hour = match[4].padStart(2, '0')
  return parseCouponDisplayDateTime(`${match[1]}/${match[2]}/${match[3]} ${hour}:${match[5]}`)
}

const parseDateTimeWithZeroSeconds = value => {
  const match = CSV_DATE_TIME_WITH_SECONDS.exec(value)
  if (!match || match[6] !== '00') return null
  const hour = match[4].padStart(2, '0')
  return parseCouponDisplayDateTime(`${match[1]}/${match[2]}/${match[3]} ${hour}:${match[5]}`)
}

const parseSpreadsheetSerial = value => {
  const match = SPREADSHEET_SERIAL.exec(value)
  if (!match) return null

  const numeric = Number(`${match[1]}.${match[2] || '0'}`)
  if (!Number.isFinite(numeric)) return null

  let days = Math.floor(numeric)
  let minuteOfDay = Math.round((numeric - days) * 1440)
  if (minuteOfDay === 1440) {
    minuteOfDay = 0
    days += 1
  }
  if (minuteOfDay < 0 || minuteOfDay > 1439) return null
  if (days < SPREADSHEET_MIN_DAY || days > SPREADSHEET_MAX_DAY) return null

  const civil = new Date(SPREADSHEET_EPOCH_UTC + days * 86400000)
  const year = civil.getUTCFullYear()
  const month = String(civil.getUTCMonth() + 1).padStart(2, '0')
  const day = String(civil.getUTCDate()).padStart(2, '0')
  const hour = String(Math.floor(minuteOfDay / 60)).padStart(2, '0')
  const minute = String(minuteOfDay % 60).padStart(2, '0')

  return parseCouponDisplayDateTime(`${day}/${month}/${year} ${hour}:${minute}`)
}

/**
 * @param {string} date `YYYY-MM-DD`
 * @param {string} time `HH:MM`
 * @returns {number | null}
 */
export const parseLocalDateTime = (date, time) => {
  if (typeof date !== 'string' || typeof time !== 'string') return null
  if (!DATE_PART.test(date) || !TIME_PART.test(time)) return null
  const timestamp = new Date(`${date}T${time}`).getTime()
  if (Number.isNaN(timestamp)) return null
  return timestamp
}

/**
 * Número, cadena vacía (`null`) o `NaN` si el texto no es numérico.
 * @param {unknown} value
 * @returns {number | null}
 */
const parseNumeric = value => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : NaN
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') return null
    if (!NUMERIC.test(trimmed)) return NaN
    return Number(trimmed)
  }
  if (value === null || value === undefined) return null
  return NaN
}

const isInactiveDiscountClear = value => {
  const parsed = parseNumeric(value)
  return parsed === null || parsed === 0
}

/**
 * Valida y construye el borrador que `createCoupon` / el lote persisten.
 * No escribe en Firebase y no consulta sucursales: el catálogo se recibe ya cargado.
 *
 * @param {Object} input
 * @param {{ storeDivisions?: string[] }} [context]
 * @returns {{ valid: boolean, errors: Object<string, string>, draft: Object | null }}
 */
export const validateAndBuildCouponDraft = (input = {}, context = {}) => {
  const errors = {}
  const source = input || {}

  const nombre = typeof source.nombre === 'string' ? source.nombre.trim() : ''
  if (!nombre) {
    errors.nombre = 'El nombre del cupón es requerido'
  } else if (!isValidCouponName(nombre)) {
    errors.nombre = 'El nombre contiene caracteres no permitidos para Firebase (. $ # [ ] /)'
  }

  const tipoDescuento = typeof source.tipoDescuento === 'string' ? source.tipoDescuento.trim() : ''
  if (tipoDescuento !== 'carrito' && tipoDescuento !== 'envio') {
    errors.tipoDescuento = 'El tipo de descuento es requerido'
  }

  if (tipoDescuento === 'carrito' || tipoDescuento === 'envio') {
    validateDiscounts(source, tipoDescuento, errors)
  }

  validateRedemptionCount(
    source.cantidadDeCanjesPorUsuario,
    'cantidadDeCanjesPorUsuario',
    'La cantidad de canjes por usuario debe ser un número entero mayor o igual a 0',
    errors
  )
  validateRedemptionCount(
    source.cantidadDeCanjesTotal,
    'cantidadDeCanjesTotal',
    'La cantidad de canjes totales debe ser un número entero mayor o igual a 0',
    errors
  )

  const fechaInicio = resolveTimestamp(
    source.fechaInicioDate,
    source.fechaInicioTime,
    'fechaInicio',
    'La fecha y hora de inicio son requeridas',
    'La fecha de inicio no es válida',
    errors
  )
  const fechaFin = resolveTimestamp(
    source.fechaFinDate,
    source.fechaFinTime,
    'fechaFin',
    'La fecha y hora de fin son requeridas',
    'La fecha de fin no es válida',
    errors
  )

  if (fechaInicio != null && fechaFin != null && fechaFin <= fechaInicio) {
    errors.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio'
  }

  const minimoParsed = parseNumeric(source.minimoDeCompra)
  if (minimoParsed === null || Number.isNaN(minimoParsed) || minimoParsed < 0) {
    errors.minimoDeCompra = 'El mínimo de compra debe ser mayor o igual a 0'
  }

  const sucursal =
    source.sucursal === null || source.sucursal === undefined ? '' : String(source.sucursal).trim()
  const allowedDivisions = new Set(
    (context.storeDivisions || []).map(division => String(division).trim()).filter(Boolean)
  )
  allowedDivisions.add('0')
  if (!sucursal) {
    errors.sucursal = 'La sucursal es requerida'
  } else if (!allowedDivisions.has(sucursal)) {
    errors.sucursal = 'La sucursal no existe en el catálogo'
  }

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors, draft: null }
  }

  return {
    valid: true,
    errors: {},
    draft: {
      nombre,
      tipoDescuento,
      descuentoCompra: tipoDescuento === 'carrito' ? parseNumeric(source.descuentoCompra) : 0,
      descuentoEnvio: tipoDescuento === 'envio' ? parseNumeric(source.descuentoEnvio) : 0,
      cantidadDeCanjesPorUsuario: parseNumeric(source.cantidadDeCanjesPorUsuario),
      cantidadDeCanjesTotal: parseNumeric(source.cantidadDeCanjesTotal),
      fechaInicio,
      fechaFin,
      minimoDeCompra: Math.round(minimoParsed),
      sucursal,
    },
  }
}

const validateDiscounts = (source, tipoDescuento, errors) => {
  if (tipoDescuento === 'carrito') {
    if (!isInactiveDiscountClear(source.descuentoEnvio)) {
      errors.descuentoEnvio = 'DescuentoEnvio debe estar vacío o ser 0 cuando Tipo es carrito.'
    }
    const amount = parseNumeric(source.descuentoCompra)
    if (amount !== null && !Number.isNaN(amount) && amount < 0) {
      errors.descuentoCompra = 'El descuento de compra debe ser mayor o igual a 0'
    } else if (amount === null || Number.isNaN(amount) || amount <= 0) {
      errors.descuentoCompra =
        'El descuento de compra debe ser mayor a $0.00 para cupones de tipo carrito'
    }
    return
  }

  if (!isInactiveDiscountClear(source.descuentoCompra)) {
    errors.descuentoCompra = 'DescuentoCompra debe estar vacío o ser 0 cuando Tipo es envio.'
  }
  const amount = parseNumeric(source.descuentoEnvio)
  if (amount !== null && !Number.isNaN(amount) && amount < 0) {
    errors.descuentoEnvio = 'El descuento de envío debe ser mayor o igual a 0'
  } else if (amount === null || Number.isNaN(amount) || amount <= 0) {
    errors.descuentoEnvio = 'El descuento de envío debe ser mayor a 0 para cupones de tipo envío'
  } else if (amount > 100) {
    errors.descuentoEnvio = 'El descuento de envío no puede ser mayor a 100'
  }
}

const validateRedemptionCount = (value, field, message, errors) => {
  const parsed = parseNumeric(value)
  if (parsed === null || Number.isNaN(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
    errors[field] = message
  }
}

const resolveTimestamp = (date, time, field, requiredMessage, invalidMessage, errors) => {
  const hasDate = typeof date === 'string' && date.trim() !== ''
  const hasTime = typeof time === 'string' && time.trim() !== ''
  if (!hasDate || !hasTime) {
    errors[field] = requiredMessage
    return null
  }
  const timestamp = parseLocalDateTime(date.trim(), time.trim())
  if (timestamp == null) {
    errors[field] = invalidMessage
    return null
  }
  const check = new Date(timestamp)
  const [year, month, day] = date.trim().split('-').map(Number)
  const [hour, minute] = time.trim().split(':').map(Number)
  if (
    check.getFullYear() !== year ||
    check.getMonth() + 1 !== month ||
    check.getDate() !== day ||
    check.getHours() !== hour ||
    check.getMinutes() !== minute
  ) {
    errors[field] = invalidMessage
    return null
  }
  return timestamp
}
